import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Award, Download, History } from "lucide-react";

export default function CertificateHistoryDialog({ open, onClose, child }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !child) return;
    setLoading(true);
    api.get(`/children/${child.id}/certificates-history`)
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, [open, child]);

  const reprint = async (cert) => {
    try {
      const r = await api.get(`/children/${child.id}/certificates-history/${cert.id}/reprint`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate-${cert.domain_id}-${cert.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تنزيل الشهادة");
    } catch (e) { toast.error(extractError(e)); }
  };

  if (!child) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cert-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <History className="w-6 h-6 text-amber-600" /> سجل شهادات الإنجاز — {child.name}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-3">كل مرة يتم فيها إنشاء "شهادة إنجاز" تُحفظ هنا. يمكنك إعادة الطباعة في أي وقت.</div>
        {loading ? (
          <div className="text-center py-10 text-slate-400 text-sm">جاري التحميل...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-14 text-slate-400 text-sm bg-slate-50 rounded-2xl">
            <Award className="w-10 h-10 mx-auto mb-2 opacity-40" />
            لا توجد شهادات محفوظة بعد.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((c) => {
              const dt = new Date(c.generated_at).toLocaleString("ar", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
              return (
                <div key={c.id} className="flex items-center gap-3 p-3 rounded-2xl border-2 hover:border-amber-300 hover:bg-amber-50/40 transition-colors" style={{ borderColor: `${c.domain_color}20` }} data-testid={`cert-entry-${c.id}`}>
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white shrink-0" style={{ background: `linear-gradient(135deg, ${c.domain_color}, ${c.domain_color}dd)` }}>
                    <Award className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-slate-900" style={{ color: c.domain_color }}>{c.domain_name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {c.percent}% · {c.achieved}/{c.total} مهارات
                      {c.mastered_count > 0 && <> · 🏆 {c.mastered_count} مدى الحياة</>}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {dt}{c.generated_by_name ? ` · بواسطة ${c.generated_by_name}` : ""}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => reprint(c)} className="gap-1 text-amber-700 border-amber-300 hover:bg-amber-100" data-testid={`reprint-cert-${c.id}`}>
                    <Download className="w-3.5 h-3.5" /> إعادة طباعة
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
